/**
 * Color Converter Tool
 */

import { converter, formatHex, formatRgb, parse } from 'culori';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

const toRgb = converter('rgb');

export const ColorConverterTool: ITransformerTool = {
  id: 'color-converter',
  name: 'Color Converter',
  description: 'Convert between HEX and RGB/HSL color formats',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['color', 'hex', 'rgb', 'hsl', 'convert'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'output',
      type: 'select',
      label: 'Output Format',
      defaultValue: 'hex',
      options: ['hex', 'rgb'],
      description: 'Format to output',
    },
    {
      name: 'uppercase',
      type: 'boolean',
      label: 'Uppercase HEX',
      defaultValue: true,
      description: 'Uppercase hex letters',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '').trim();
    if (!text) return '';

    const parsed = parse(text);
    const rgb = parsed ? toRgb(parsed) : null;
    if (!rgb) throw ToolError.validation('Unrecognized color format. Try #RRGGBB or rgb(...)');

    const output = String(config.output ?? 'hex');
    if (output === 'rgb') return formatRgb(rgb);

    const uppercase = config.uppercase !== false;
    const hex = formatHex({ ...rgb, alpha: 1 });
    return uppercase ? hex.toUpperCase() : hex.toLowerCase();
  },
};
